import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixCloudAutocmsV3CollectionMetadataServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'editor._base_domain_': [
      {
        srcPath: '/_api/autocms/v3/collection-metadata',
        destPath: '/v3/collection-metadata',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/autocms/v3/collection-metadata',
        destPath: '/v3/collection-metadata',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/autocms/v3/collection-metadata',
        destPath: '/v3/collection-metadata',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/autocms/v3/collection-metadata',
        destPath: '/v3/collection-metadata',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME =
  '@wix/auto_sdk_autocms-collection-metadata-service_autocms';

/**
 * Creates a new collection metadata instance.
 *
 * In case of an error, this endpoint returns the following codes:
 * - `METADATA_EXISTS`: Metadata already exists for the specified collection.
 */
export function createCollectionMetadata(
  payload: object
): RequestOptionsFactory<any> {
  function __createCollectionMetadata({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.cloud.autocms.v3.collection_metadata',
      method: 'POST' as any,
      methodFqn:
        'wix.cloud.autocms.v3.CollectionMetadataService.CreateCollectionMetadata',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudAutocmsV3CollectionMetadataServiceUrl({
        protoPath: '/v3/collection-metadata',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __createCollectionMetadata;
}

/**
 * Gets a collection metadata instance.
 *
 * In case of an error, this endpoint returns the following codes:
 * - `ITEM_NOT_FOUND`: No metadata exists for the specified collection.
 */
export function getCollectionMetadata(
  payload: object
): RequestOptionsFactory<any> {
  function __getCollectionMetadata({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.cloud.autocms.v3.collection_metadata',
      method: 'GET' as any,
      methodFqn:
        'wix.cloud.autocms.v3.CollectionMetadataService.GetCollectionMetadata',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudAutocmsV3CollectionMetadataServiceUrl({
        protoPath: '/v3/collection-metadata/{dataCollectionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getCollectionMetadata;
}

/**
 * Updates the metadata for the specified collection.
 *
 * In case of an error, this endpoint returns the following codes:
 * - `ITEM_NOT_FOUND`: No metadata exists for the specified collection.
 */
export function updateCollectionMetadata(
  payload: object
): RequestOptionsFactory<any> {
  function __updateCollectionMetadata({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.cloud.autocms.v3.collection_metadata',
      method: 'PATCH' as any,
      methodFqn:
        'wix.cloud.autocms.v3.CollectionMetadataService.UpdateCollectionMetadata',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudAutocmsV3CollectionMetadataServiceUrl({
        protoPath: '/v3/collection-metadata/{dataCollectionId}',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __updateCollectionMetadata;
}

/** Completely replaces all existing collection metadata with the provided metadata. */
export function replaceCollectionMetadata(
  payload: object
): RequestOptionsFactory<any> {
  function __replaceCollectionMetadata({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.cloud.autocms.v3.collection_metadata',
      method: 'PUT' as any,
      methodFqn:
        'wix.cloud.autocms.v3.CollectionMetadataService.ReplaceCollectionMetadata',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudAutocmsV3CollectionMetadataServiceUrl({
        protoPath: '/v3/collection-metadata',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __replaceCollectionMetadata;
}

/**
 * Deletes a collection metadata instance. (1)
 * Deletes the specified collection's metadata. (2)
 */
export function deleteCollectionMetadata(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteCollectionMetadata({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.cloud.autocms.v3.collection_metadata',
      method: 'DELETE' as any,
      methodFqn:
        'wix.cloud.autocms.v3.CollectionMetadataService.DeleteCollectionMetadata',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudAutocmsV3CollectionMetadataServiceUrl({
        protoPath: '/v3/collection-metadata/{dataCollectionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteCollectionMetadata;
}

/** Lists all collection metadata. */
export function listCollectionMetadata(
  payload: object
): RequestOptionsFactory<any> {
  function __listCollectionMetadata({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.cloud.autocms.v3.collection_metadata',
      method: 'GET' as any,
      methodFqn:
        'wix.cloud.autocms.v3.CollectionMetadataService.ListCollectionMetadata',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudAutocmsV3CollectionMetadataServiceUrl({
        protoPath: '/v3/collection-metadata',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listCollectionMetadata;
}
